import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClientsSearchDialogComponent,
  IClientDto
} from '@howdeniberia/client-search';
import {
  ControlErrorDirective,
  HowdenAlertService,
  PageTitleComponent,
  UIBlockerService
} from '@howdeniberia/core-front';
import { forkJoin } from 'rxjs';
import { filter, finalize, map, switchMap, take } from 'rxjs/operators';
import { YES_NO_VALUES } from 'src/app/core/constants';
import {
  IClient,
  IProjectOutputDto
} from 'src/app/core/models/api/sane-project/arpa-model';
import { CalculatorProjectService } from 'src/app/core/services/api/calculator';
import { EditProjectContract } from 'src/app/core/services/api/edit/edit-project.contract';
import { SaneProjectContract } from 'src/app/core/services/api/projects/sane-project.contract';
import { SharedService } from 'src/app/core/services/api/shared/shared.service';
import { toNumber } from 'src/app/shared/utils';
import { ProjectDraftMapper } from '../mappers/project-draft.mapper';
import { ProjectMapper } from '../mappers/project.mapper';
import { ProjectViewModel } from '../models/project.viewmodel';
import { CalculatorProductViewModel } from '../sane-project-calculator/models/calculator-product.viewmodel';

@Component({
  selector: 'howden-sane-project-edit',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDatepickerModule,
    ControlErrorDirective,
    PageTitleComponent
  ],
  templateUrl: './sane-project-edit.component.html',
  styleUrls: ['./sane-project-edit.component.scss']
})
export class SaneProjectEditComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly editSrv = inject(EditProjectContract);
  private readonly saneProjectSrv = inject(SaneProjectContract);
  private readonly uiBlockerSrv = inject(UIBlockerService);
  private readonly router = inject(Router);
  private readonly sharedSrv = inject(SharedService);
  private readonly calcSrv = inject(CalculatorProjectService);
  private readonly dialog = inject(MatDialog);
  private readonly alertSrv = inject(HowdenAlertService);
  private readonly destroyRef = inject(DestroyRef);

  public project = new ProjectViewModel(this.fb);
  private mastersLoaded = false;

  public isEditMode = false;

  public readonly minEffectiveDate = new Date(1900, 0, 1);

  private readonly defaultSelects = {
    activity: 'Cuenta Propia',
    paymentType: 'Mensual',
    sport: 'Ninguno'
  };

  compareByIdBoolean = this.calcSrv.compareByIdBoolean.bind(this.calcSrv);
  compareByIdNumber = (a: unknown, b: unknown): boolean => {
    const toStr = (v: any) => (v === null || v === undefined ? '' : String(v));
    return toStr(a) === toStr(b);
  };
  compareById = (a: unknown, b: unknown): boolean => a === b;

  get hasClientSelected(): boolean {
    const ctrl = this.project.edit.getControl(this.project.edit.cClientId);
    const v = ctrl?.value;
    return v !== null && v !== undefined && String(v).trim() !== '';
  }

  get hasAnyProductSelected(): boolean {
    const e = this.project.edit;
    const names = [
      e.cIndustryLife,
      e.cIndustryAccident,
      e.cIndustryTemporaryIncapacity,
      e.cIndustryDependency,
      e.cIndustryDeath
    ];
    return names.some(n => !!e.getControl(n)?.value === true);
  }

  get hasClientProfession(): boolean {
    const e = this.project.edit;
    const desc = e.getControl(e.cClientProfession)?.value;
    const codeRaw = e.getControl(e.cClientProfessioncode)?.value;

    const hasDesc = String(desc ?? '').trim().length > 0;

    const n = Number(codeRaw);
    const hasCode = Number.isFinite(n) && n > 0;

    return hasDesc || hasCode;
  }

  get canClickTariff(): boolean {
    return (
      this.project.edit.form.valid
      && this.hasClientSelected
      && this.hasAnyProductSelected
      && this.hasClientProfession
    );
  }

  private minDateValidator = (min: Date) => {
    const minTime = new Date(min.getFullYear(), min.getMonth(), min.getDate()).getTime();
    return (ctrl: AbstractControl): ValidationErrors | null => {
      const v = ctrl.value;
      if (!v) { return null; }
      const d = v instanceof Date ? v : new Date(String(v));
      if (Number.isNaN(d.getTime())) { return { dateInvalid: true }; }
      const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      return dOnly < minTime ? { minDate: { min: min.toISOString().slice(0, 10) } } : null;
    };
  };

  ngOnInit(): void {
    const initialId = Number(this.route.snapshot.paramMap.get('id') ?? 0);
    this.isEditMode = initialId > 0;

    const fromCalculator = !!(
      history.state && history.state.fromCalculator === true
    );

    this.resetViewModelWithProjectId(initialId);

    this.coerceToNullOnChange(
      this.project.edit.cContributionReta,
      this.project.edit.cContributionGeneralRegime
    );

    const nameCtrl = this.project.form.get('edit.projectName');
    if (nameCtrl) {
      nameCtrl.addValidators(Validators.maxLength(255));
      nameCtrl.updateValueAndValidity({ emitEvent: false });
    }

    if (!this.isEditMode) {
      const effCtrl = this.project.form.get('edit.projectEffectiveDate');
      if (effCtrl) {
        effCtrl.addValidators(this.minDateValidator(this.minEffectiveDate));
        effCtrl.updateValueAndValidity({ emitEvent: false });
      }
    }

    if (!this.isEditMode) {
      const hasCachedModel = !!this.sharedSrv.getModel();
      const hasDraft = !!this.sharedSrv.loadDraft();
      const keepCache = fromCalculator && (hasCachedModel || hasDraft);

      if (!keepCache) {
        this.sharedSrv.clearModel();
        this.sharedSrv.clearDto();
        this.sharedSrv.clearDraft();
        this.mastersLoaded = false;
        this.hardResetForNewClientSelection();
      }
    }

    this.sharedSrv.project$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((cached) => {
        const cachedId = cached?.edit?.getControl(cached.edit.cProjectId)
          ?.value as number | undefined;

        const shouldUseCache
          = (this.isEditMode && cached && cachedId === initialId)
          || (!this.isEditMode
            && fromCalculator
            && !!(cached || this.sharedSrv.loadDraft()));

        if (shouldUseCache && cached) {
          this.project = cached;
        }
        this.loadMasterDataAndMaybeProject(initialId);
      });

    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const newId = Number(params['id'] ?? 0);
        this.isEditMode = newId > 0;

        const current = this.sharedSrv.getModel();
        const currentId = current?.edit?.getControl(current.edit.cProjectId)
          ?.value as number | undefined;

        const navigatingToNew = !this.isEditMode;
        const hasCachedModel = !!current || !!this.sharedSrv.loadDraft();

        if (navigatingToNew) {
          if (!fromCalculator || !hasCachedModel) {
            this.sharedSrv.clearModel();
            this.sharedSrv.clearDto();
            this.sharedSrv.clearDraft();
            this.mastersLoaded = false;
            this.project.edit
              .getControl(this.project.edit.cProjectId)
              .setValue(newId);
            this.hardResetForNewClientSelection();
            return;
          }
        }

        if (!current || currentId !== newId) {
          this.sharedSrv.clearModel();
          this.sharedSrv.clearDto();
          this.sharedSrv.clearDraft();
          this.mastersLoaded = false;
        }
        this.project.edit
          .getControl(this.project.edit.cProjectId)
          .setValue(newId);
      });
  }

  private loadMasterDataAndMaybeProject(projectId: number): void {
    if (this.mastersLoaded && this.sharedSrv.getDto()) {
      return;
    }

    this.uiBlockerSrv.block();

    forkJoin([
      this.editSrv.getPaymentTypes(),
      this.editSrv.getActivities(),
      this.editSrv.getSports(),
      this.editSrv.getLivingStandards(),
      this.editSrv.getChildren(),
      this.editSrv.getDeadlinesIPT(),
      this.editSrv.getContributionsRETA(),
      this.editSrv.getContributionsGeneralReg(),
      this.editSrv.getMutualIncomes(),
      this.editSrv.getMotorbikes(),
      this.editSrv.getMaritalStatuses()
    ])
      .pipe(
        map(
          ([
            paymentTypes,
            activities,
            sports,
            livingStandards,
            children,
            deadlineIPTs,
            contributionRETAs,
            contributionGeneralRegs,
            mutualIncomes,
            motorbikes,
            maritalStatuses
          ]) => ({
            paymentTypes: this.uniqueById(this.sortByDescription(paymentTypes)),
            activities: this.uniqueById(this.sortByDescription(activities)),
            sports: this.uniqueById(this.sortByDescription(sports)),
            livingStandards: this.uniqueById(
              this.sortByDescriptionNumeric(livingStandards)
            ),
            children: this.uniqueById(this.sortByDescriptionNumeric(children)),
            deadlineIPTs: this.uniqueById(this.sortByDescription(deadlineIPTs)),
            contributionRETAs: this.uniqueById(
              this.sortByDescription(contributionRETAs)
            ),
            contributionGeneralRegs: this.uniqueById(
              this.sortByDescription(contributionGeneralRegs)
            ),
            mutualIncomes: this.uniqueById(
              this.sortByDescription(mutualIncomes)
            ),
            motorbikes: this.uniqueById(this.sortByDescription(motorbikes)),
            maritalStatuses: this.uniqueById(
              this.sortByDescription(maritalStatuses)
            )
          })
        ),
        finalize(() => this.uiBlockerSrv.unblock()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        (masters) => {
          Object.assign(this.project.edit, {
            ...masters,
            yesNoValues: this.uniqueByKey(YES_NO_VALUES)
          });

          this.mastersLoaded = true;

          this.setDefaultRiskTogglesToNo();
          this.setDefaultMastersSelections();

          if (projectId > 0 && !this.sharedSrv.getDto()) {
            this.loadProjectData(projectId);
          }
        },
        (err) => console.error('Error loading master data', err)
      );
  }

  private setDefaultRiskTogglesToNo(): void {
    if (this.isEditMode) {
      return;
    }

    const e = this.project.edit;

    const setIfEmpty = (controlName: string, value: unknown) => {
      const ctrl = e.getControl(controlName);
      const current = ctrl.value;
      if (current === null || current === undefined || current === '') {
        ctrl.setValue(value as any, { emitEvent: false });
      }
    };

    const noYesNoKey = this.project.edit.yesNoValues?.find((x) =>
      String(x.value).trim().toLowerCase().startsWith('no')
    )?.key;

    if (noYesNoKey !== null && noYesNoKey !== undefined) {
      setIfEmpty(e.cLifeDrivingForce, noYesNoKey);
      setIfEmpty(e.cLifeScaffoldRoof, noYesNoKey);
      setIfEmpty(e.cLifeSpecialRisks, noYesNoKey);
    }

    const noMotorbikeId = this.project.edit.motorbikes?.find((m) => {
      const d = String(m?.description ?? '')
        .trim()
        .toLowerCase();
      return (
        d === 'no'
        || d === 'none'
        || /^ningun|ninguna/.test(d)
        || /sin\s*moto/.test(d)
      );
    })?.id;

    if (noMotorbikeId !== null && noMotorbikeId !== undefined) {
      setIfEmpty(e.cLifeMotorbike, noMotorbikeId);
    }

    this.project.form.updateValueAndValidity({ emitEvent: false });
  }

  private loadProjectData(projectId: number): void {
    this.uiBlockerSrv.block();

    this.saneProjectSrv
      .getProjectById(projectId)
      .pipe(
        finalize(() => this.uiBlockerSrv.unblock()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        (dto: IProjectOutputDto) => {
          ProjectMapper.mapDtoToViewModel(this.project, dto);
          this.sharedSrv.setModel(this.project);
          this.sharedSrv.setDto(dto);

          this.sharedSrv.setCanSeeTrace(!!dto.canSeeTrace);
          this.sharedSrv.setCanSeeCoegTI(!!dto.canSeePmTICoeg);

          const draft = ProjectDraftMapper.toDraft(this.project);
          this.sharedSrv.saveDraft(draft);
        },
        (err) => console.error('Error loading project', err)
      );
  }

  private normalizeRegimeSelects(): void {
    const e = this.project.edit;
    const fix = (name: string) => {
      const ctrl = e.getControl(name);
      const v = ctrl.value;
      if (v === 0 || v === '0' || v === '' || v === undefined) {
        ctrl.setValue(null, { emitEvent: false });
      }
    };
    fix(e.cContributionReta);
    fix(e.cContributionGeneralRegime);
  }

  onAccept(): void {
    const editGroup = this.project.form.get('edit');

    this.normalizeRegimeSelects();

    if (
      editGroup?.valid
      && !this.editSrv.checkClient(this.project)
      && !this.editSrv.checkCondition(this.project)
    ) {
      this.sharedSrv.setModel(this.project);
      const draft = ProjectDraftMapper.toDraft(this.project);
      this.sharedSrv.saveDraft(draft);
      this.router.navigate(['sane-projects/calculator']);
    }
  }

  onSearch(): void {
    if (this.isEditMode) {
      return;
    }
    this.openClientsSearchDialog();
  }

  onCancel(): void {
    this.router.navigate(['/sane-projects/search']);
  }

  openClientsSearchDialog(): void {
    const dialogRef = this.dialog.open(ClientsSearchDialogComponent, {
      height: '95%',
      width: '95%',
      maxHeight: '720px'
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((selected: IClientDto | null) => !!selected?.id),
        switchMap((selected) => this.fetchClient((selected as IClientDto).id))
      )
      .subscribe({
        next: (client) => {
          this.hardResetForNewClientSelection();
          this.applyClientToForm(client);
        },
        error: (err) => console.error('Error fetching client details', err)
      });
  }

  clientSelected(client: IClientDto): void {
    if (this.isEditMode) {
      return;
    }
    if (!client?.id) {
      return;
    }

    this.fetchClient(client.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (c) => {
          this.hardResetForNewClientSelection();
          this.applyClientToForm(c);
        },
        error: (err) => console.error('Error fetching client details', err)
      });
  }

  private fetchClient(clientId: number) {
    this.uiBlockerSrv.block();
    return this.saneProjectSrv
      .getClient(clientId)
      .pipe(finalize(() => this.uiBlockerSrv.unblock()));
  }

  private hasValidBirthDate(value: unknown): boolean {
    if (!value) {
      return false;
    }
    const d = value instanceof Date ? value : new Date(String(value));
    return !Number.isNaN(d.getTime());
  }

  private clearClientSelection(): void {
    const edit = this.project.edit;

    const toClear = [
      edit.cClientId,
      edit.cClientName,
      edit.cClientBirthdate,
      edit.cClientAge,
      edit.cClientActuarialage,
      edit.cClientProfession,
      edit.cClientProvince,
      edit.cClientProfessioncode,
      edit.cClientOfficename,
      edit.cClientOfficecode,
      edit.cClientOfficealias,
      edit.cClientAddresszipcode,
      edit.cClientSex
    ];

    for (const ctrlName of toClear) {
      const ctrl = edit.getControl(ctrlName);
      ctrl.reset('', { emitEvent: false });
    }

    this.project.form.markAsPristine();
    this.project.form.markAsUntouched();
    this.project.form.updateValueAndValidity({ emitEvent: false });
  }

  private toDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }
    const d = new Date(String(value));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private toOneZeroOrNull(v: unknown): '1' | '0' | null {
    if (v === true || String(v).trim() === '1') {
      return '1';
    }
    if (v === false || String(v).trim() === '0') {
      return '0';
    }
    return null;
  }

  private applyClientToForm(data: IClient): void {
    const birthDate = this.toDate(data?.birthDate);

    if (!birthDate) {
      this.alertSrv.info(
        'Información',
        'El cliente seleccionado no tiene fecha de nacimiento. No se puede asignar y se limpiará la selección.\nPor favor, rellene en la ficha del cliente la fecha de nacimiento para poder seleccionarlo.',
        true,
        false
      );
      this.clearClientSelection();
      this.resetPricingStateAfterClientChange();
      return;
    }

    const edit = this.project.edit;

    const setOpt = (ctrlName: string, val: unknown) => {
      const ctrl = edit.getControl(ctrlName);
      const empty = val === null || val === undefined || val === '';
      ctrl.setValue(empty ? null : val, { emitEvent: false });
    };

    edit.getControl(edit.cClientName).setValue(data.client ?? '', { emitEvent: false });
    edit.getControl(edit.cClientId).setValue(data.clientId ?? '', { emitEvent: false });
    edit.getControl(edit.cClientBirthdate).setValue(birthDate, { emitEvent: false });
    edit.getControl(edit.cClientActuarialage).setValue(toNumber(data.actuarialAge), { emitEvent: false });

    const profCodeNum = Number(data.professionCode);
    const normalizedProfCode = Number.isFinite(profCodeNum) && profCodeNum > 0 ? profCodeNum : null;
    edit.getControl(edit.cClientProfessioncode).setValue(normalizedProfCode, { emitEvent: false });
    edit.getControl(edit.cClientProfession).setValue(data.profession ?? '', { emitEvent: false });

    edit.getControl(edit.cClientProvince).setValue(data.officeName ?? '', { emitEvent: false });
    edit.getControl(edit.cClientOfficename).setValue(data.officeName ?? '', { emitEvent: false });
    edit.getControl(edit.cClientOfficealias).setValue(data.officeAlias ?? '', { emitEvent: false });
    edit.getControl(edit.cClientOfficecode).setValue(data.officeCode ?? '', { emitEvent: false });
    edit.getControl(edit.cClientAddresszipcode).setValue(data.addressPostalCode ?? '', { emitEvent: false });
    edit.getControl(edit.cClientSex).setValue(toNumber(data.clientSex) ?? 0, { emitEvent: false });

    setOpt(edit.cDeadlineIpt, toNumber(data.deadlineIPT));
    setOpt(edit.cLivingStandard, toNumber(data.lifeLevel));
    setOpt(edit.cMinorChildren, toNumber(data.children));
    setOpt(edit.cMutualIncome, toNumber(data.mutual));
    setOpt(edit.cContributionReta, toNumber(data.retaQuote));
    setOpt(edit.cContributionGeneralRegime, toNumber(data.generalRegimeQuote));
    setOpt(edit.cMarried, this.toOneZeroOrNull(data.married));

    this.sharedSrv.setCanSeeTrace(!!data.canSeeTrace);
    this.sharedSrv.setCanSeeCoegTI(!!data.canSeePmTICoeg);

    this.setDefaultMastersSelections();

    if (!this.hasClientProfession) {
      this.alertSrv.info(
        'Información',
        'Este candidato/cliente no tiene una profesión registrada. Para poder realizar la tarificación ARPA, es necesario que accedas a su ficha y completes este dato.',
        true,
        false
      );
      this.clearClientSelection();
      this.resetPricingStateAfterClientChange();
      return;
    }

    this.project.form.markAsDirty();
    this.project.form.updateValueAndValidity();

    this.resetPricingStateAfterClientChange();
  }

  private resetViewModelWithProjectId(projectId: number): void {
    this.project = new ProjectViewModel(this.fb);
    this.project.edit
      .getControl(this.project.edit.cProjectId)
      .setValue(projectId);
  }

  private resetPricingStateAfterClientChange(): void {
    if (this.isEditMode) {
      return;
    }

    this.resetAllCalculatorBranches();

    this.sharedSrv.clearDto();

    this.sharedSrv.setModel(this.project);
    const draft = ProjectDraftMapper.toDraft(this.project);
    this.sharedSrv.saveDraft(draft);
  }

  private resetAllCalculatorBranches(): void {
    this.resetCalculatorBranch((this.project as any)?.calculatorLife);
    this.resetCalculatorBranch((this.project as any)?.calculatorAccidents);
    this.resetCalculatorBranch((this.project as any)?.calculatorIT);
    this.resetCalculatorBranch((this.project as any)?.calculatorDependence);
    this.resetCalculatorBranch((this.project as any)?.calculatorDeaths);
  }

  private resetCalculatorBranch(branch: any): void {
    if (!branch || typeof branch !== 'object') {
      return;
    }
    Object.values(branch).forEach((vm) => {
      if (vm instanceof CalculatorProductViewModel) {
        this.resetSingleCalculatorVm(vm);
      } else if (vm && typeof vm === 'object') {
        this.resetCalculatorBranch(vm);
      }
    });
  }

  private resetSingleCalculatorVm(vm: CalculatorProductViewModel): void {
    for (const [ctrlName, cfg] of Object.entries(vm.preset.controls)) {
      vm.form.get(ctrlName)?.reset(cfg.defaultValue, { emitEvent: false });
    }
    vm.form.markAsPristine();
    vm.form.markAsUntouched();

    vm.bonus = 0;
    vm.chargeable = true;
    vm.productName = '';
    vm.trace = '';
    vm.maximumCapital = 0;
    vm.hasTelesubscription = false;
    vm.telesubscriptionDirect = false;
    vm.telesubscriptionSingleQuestion = false;
    vm.summaryCapitals = '';

    vm.messages = [];
  }

  private resetEditFormToDefaults(): void {
    const e = this.project.edit;

    const defaults: Record<string, any> = {
      [e.cClientId]: '',
      [e.cClientName]: '',
      [e.cClientBirthdate]: '',
      [e.cClientAge]: '',
      [e.cClientProfession]: '',
      [e.cClientProvince]: '',
      [e.cClientActuarialage]: '',
      [e.cClientProfessioncode]: '',
      [e.cClientOfficename]: '',
      [e.cClientOfficecode]: '',
      [e.cClientAddresszipcode]: '',
      [e.cClientOfficealias]: '',
      [e.cClientSex]: '',

      [e.cProjectId]: this.isEditMode ? e.getControl(e.cProjectId).value : '',
      [e.cProjectName]: '',
      [e.cEffectiveDate]: new Date(),
      [e.cPaymentType]: null,
      [e.cActivity]: null,
      [e.cSport]: null,

      [e.cLifeDrivingForce]: null,
      [e.cLifeScaffoldRoof]: null,
      [e.cLifeSpecialRisks]: null,
      [e.cLifeMotorbike]: null,

      [e.cIndustryLife]: true,
      [e.cIndustryAccident]: true,
      [e.cIndustryTemporaryIncapacity]: true,
      [e.cIndustryDependency]: true,
      [e.cIndustryWholeLife]: true,
      [e.cIndustryDeath]: true,

      [e.cLivingStandard]: null,
      [e.cDeadlineIpt]: null,
      [e.cContributionReta]: null,
      [e.cContributionGeneralRegime]: null,
      [e.cMarried]: null,
      [e.cMutualIncome]: null,
      [e.cMinorChildren]: null
    };

    e.form.reset(defaults, { emitEvent: false });

    e.getControl(e.cClientId).disable({ emitEvent: false });
    e.getControl(e.cClientName).disable({ emitEvent: false });
    e.getControl(e.cClientBirthdate).disable({ emitEvent: false });
    e.getControl(e.cClientAge).disable({ emitEvent: false });
    e.getControl(e.cClientProfession).disable({ emitEvent: false });
    e.getControl(e.cClientProvince).disable({ emitEvent: false });
    e.getControl(e.cClientActuarialage).disable({ emitEvent: false });
    e.getControl(e.cProjectId).disable({ emitEvent: false });
    e.getControl(e.cClientSex).disable({ emitEvent: false });

    this.project.form.markAsPristine();
    this.project.form.markAsUntouched();
    this.project.form.updateValueAndValidity({ emitEvent: false });

    if (this.mastersLoaded) {
      this.setDefaultRiskTogglesToNo();
      this.setDefaultMastersSelections();
    }
  }

  private hardResetForNewClientSelection(): void {
    if (this.isEditMode) {
      return;
    }

    this.sharedSrv.clearDto();
    this.sharedSrv.clearDraft();
    this.sharedSrv.setCanSeeTrace(false);
    this.sharedSrv.setCanSeeCoegTI(false);

    this.resetAllCalculatorBranches();

    this.resetEditFormToDefaults();

    this.sharedSrv.setModel(this.project);
    const draft = ProjectDraftMapper.toDraft(this.project);
    this.sharedSrv.saveDraft(draft);
  }

  private sortByDescription<T extends { description?: string }>(
    arr: T[] | null | undefined
  ): T[] {
    if (!arr?.length) {
      return [];
    }
    return arr
      .slice()
      .sort((a, b) => (a.description ?? '').localeCompare(b.description ?? ''));
  }

  private sortByDescriptionNumeric<T extends { description?: string }>(
    arr: T[] | null | undefined
  ): T[] {
    if (!arr?.length) {
      return [];
    }

    return arr.slice().sort((a, b) => {
      const ad = (a.description ?? '').trim();
      const bd = (b.description ?? '').trim();

      const an = Number(ad);
      const bn = Number(bd);

      const aIsNum = ad !== '' && !Number.isNaN(an);
      const bIsNum = bd !== '' && !Number.isNaN(bn);

      if (aIsNum && bIsNum) {
        return an - bn;
      }
      if (aIsNum && !bIsNum) {
        return -1;
      }
      if (!aIsNum && bIsNum) {
        return 1;
      }

      return ad.localeCompare(bd);
    });
  }

  private uniqueBy<T>(
    arr: T[] | null | undefined,
    keySelector: (x: T) => string
  ): T[] {
    if (!arr?.length) {
      return [];
    }
    const seen = new Set<string>();
    const out: T[] = [];
    for (const it of arr) {
      const key = keySelector(it);
      if (!seen.has(key)) {
        seen.add(key);
        out.push(it);
      }
    }
    return out;
  }

  private uniqueById<T extends { id: unknown }>(
    arr: T[] | null | undefined
  ): T[] {
    return this.uniqueBy(arr, (x) => String(x?.id ?? ''));
  }

  private uniqueByKey<T extends { key: unknown }>(
    arr: T[] | null | undefined
  ): T[] {
    return this.uniqueBy(arr, (x) => String(x?.key ?? ''));
  }

  private coerceToNullOnChange(...controlNames: string[]): void {
    const e = this.project.edit;
    for (const name of controlNames) {
      const ctrl = e.getControl(name);
      ctrl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((v) => {
          const isEmptyLike
            = v === 0 || v === '0' || v === '' || v === undefined;
          if (isEmptyLike) {
            ctrl.setValue(null, { emitEvent: false });
            this.project.form.updateValueAndValidity({ emitEvent: false });
          }
        });
    }
  }

  private normalizeText(v: unknown): string {
    return String(v ?? '')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .trim()
      .toLowerCase();
  }

  private setDefaultSelectIfEmpty(
    controlName: string,
    list: Array<{ id: unknown; description?: string }>,
    desiredDescription: string
  ): void {
    const e = this.project.edit;
    const ctrl = e.getControl(controlName);
    const current = ctrl.value;

    const isEmpty
      = current === null || current === undefined || current === '' || current === 0 || current === '0';

    if (!isEmpty) {
      return;
    }
    if (!Array.isArray(list) || list.length === 0) {
      return;
    }

    const desiredNorm = this.normalizeText(desiredDescription);
    const found = list.find(i => this.normalizeText(i?.description) === desiredNorm);
    if (found && found.id !== undefined && found.id !== null) {
      ctrl.setValue(found.id, { emitEvent: false });
    }
  }

  private setDefaultMastersSelections(): void {
    if (this.isEditMode) {
      return;
    }

    const e = this.project.edit;

    this.setDefaultSelectIfEmpty(
      e.cActivity,
      this.project.edit.activities ?? [],
      this.defaultSelects.activity
    );

    this.setDefaultSelectIfEmpty(
      e.cPaymentType,
      this.project.edit.paymentTypes ?? [],
      this.defaultSelects.paymentType
    );

    this.setDefaultSelectIfEmpty(
      e.cSport,
      this.project.edit.sports ?? [],
      this.defaultSelects.sport
    );

    this.project.form.updateValueAndValidity({ emitEvent: false });
  }
}
